// Lib
import React from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { useActiveTableCallback } from '../../../../../element/table/store/hotTableStoreHooks';
import {
    updateTableCellTextAlignment,
    updateTableCellVerticalAlignment,
} from '../../../../../element/table/actions/hotTableActions';

// Components
import ToolbarPopup from '../../toolbarPopup/ToolbarPopup';
import Icon from '../../../../../components/icons/Icon';
import Button from '../../../../../components/buttons/Button';

// Constants
import { PopupIds } from '../../../../../components/popupPanel/popupConstants';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { TextAlignment, VerticalAlignment } from '../../../../../../common/table/CellTypeConstants';

import './TableCellAlignmentToolbarPopup.scss';

type TableCellAlignmentToolbarPopupContentsProps = {
    commonTextAlignment: string;
    commonVerticalAlignment: string;
    element: ImMNElement;
};

/**
 * NOTE: Structuring this as a separate component to avoid rendering the popup contents
 * when the popup isn't open.
 */
const TableCellAlignmentToolbarPopupContents: React.FC<TableCellAlignmentToolbarPopupContentsProps> = ({
    commonTextAlignment,
    commonVerticalAlignment,
    element,
}) => {
    const elementId = getElementId(element);

    const dispatch = useDispatch();

    const updateCellTextAlignment = useActiveTableCallback(
        (activeTable, textAlignment: TextAlignment) => {
            dispatch(
                updateTableCellTextAlignment({
                    hotInstance: activeTable,
                    elementId,
                    textAlignment,
                }),
            );
        },
        [elementId],
    );

    const updateCellVerticalAlignment = useActiveTableCallback(
        (activeTable, verticalAlignment: VerticalAlignment) => {
            dispatch(
                updateTableCellVerticalAlignment({
                    hotInstance: activeTable,
                    elementId,
                    verticalAlignment,
                }),
            );
        },
        [elementId],
    );

    return (
        <>
            <ul className="cell-alignment-options">
                <li>
                    <Button
                        className={classNames('cell-alignment-options-button', {
                            active: commonTextAlignment === TextAlignment.LEFT,
                        })}
                        onClickFn={() => updateCellTextAlignment(TextAlignment.LEFT)}
                    >
                        <Icon name="toolbar-align-left" />
                    </Button>
                </li>
                <li>
                    <Button
                        className={classNames('cell-alignment-options-button', {
                            active: commonTextAlignment === TextAlignment.CENTER,
                        })}
                        onClickFn={() => updateCellTextAlignment(TextAlignment.CENTER)}
                    >
                        <Icon name="toolbar-align-center" />
                    </Button>
                </li>
                <li>
                    <Button
                        className={classNames('cell-alignment-options-button', {
                            active: commonTextAlignment === TextAlignment.RIGHT,
                        })}
                        onClickFn={() => updateCellTextAlignment(TextAlignment.RIGHT)}
                    >
                        <Icon name="toolbar-align-right" />
                    </Button>
                </li>
            </ul>

            <div className="divider" />

            <ul className="cell-alignment-options">
                <li>
                    <Button
                        className={classNames('cell-alignment-options-button', {
                            active: commonVerticalAlignment === VerticalAlignment.TOP,
                        })}
                        onClickFn={() => updateCellVerticalAlignment(VerticalAlignment.TOP)}
                    >
                        <Icon name="toolbar-align-top" />
                    </Button>
                </li>
                <li>
                    <Button
                        className={classNames('cell-alignment-options-button', {
                            active: commonVerticalAlignment === VerticalAlignment.MIDDLE,
                        })}
                        onClickFn={() => updateCellVerticalAlignment(VerticalAlignment.MIDDLE)}
                    >
                        <Icon name="toolbar-align-middle" />
                    </Button>
                </li>
                <li>
                    <Button
                        className={classNames('cell-alignment-options-button', {
                            active: commonVerticalAlignment === VerticalAlignment.BOTTOM,
                        })}
                        onClickFn={() => updateCellVerticalAlignment(VerticalAlignment.BOTTOM)}
                    >
                        <Icon name="toolbar-align-bottom" />
                    </Button>
                </li>
            </ul>
        </>
    );
};

type TableCellAlignmentToolbarPopupProps = TableCellAlignmentToolbarPopupContentsProps & {
    buttonSelector: string;
    closePopup: () => void;
};

const TableCellAlignmentToolbarPopup: React.FC<TableCellAlignmentToolbarPopupProps> = ({
    commonTextAlignment,
    commonVerticalAlignment,
    element,
    buttonSelector,
    closePopup,
}) => (
    <ToolbarPopup
        closePopup={closePopup}
        buttonSelector={buttonSelector}
        showArrow
        popupId={PopupIds.TABLE_CELL_ALIGNMENT_TOOL}
        className="TableCellAlignmentToolbarPopup"
    >
        <TableCellAlignmentToolbarPopupContents
            commonTextAlignment={commonTextAlignment}
            commonVerticalAlignment={commonVerticalAlignment}
            element={element}
        />
    </ToolbarPopup>
);

export default TableCellAlignmentToolbarPopup;
